import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';
import { Loader, useQueryFetch } from 'sonata-react-components';
import {
  createLineItem,
  getCampaignPlan,
  getInsertionOrder,
  getLineItem,
  LineItem,
  LineItemEntity,
  updateLineItem
} from 'sonata-static-storage/lib/entities';

import { LineItemWizard } from '@/components/common/LineItemWizard';
import { useModals, useNavigate } from '@/router';
import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors.ts';
import { getSession } from '@/store/global/session/selectors.ts';
import { getUser } from '@/store/global/user/selectors.ts';
import { getUserRoles } from '@/store/global/userRoles/selectors.ts';
import { CampaignsStore, useStore as useCampaignStore } from '@/store/local/campaigns';

type State = {
  globalLoading: boolean;
  isLoading: boolean;
  modalStatus: boolean;
  options: {
    inheritCreativeConfigFromIO: boolean;
    inheritMapConfigFromIO: boolean;
    numberOfEntitiesToCreate: number;
  };
};

export default function LineItemSettingsModal() {
  const modals = useModals();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currencyInfo = useGlobalStore(getBaseCurrency);
  const user = useGlobalStore(getUser);
  const session = useGlobalStore(getSession);
  const userRoles = useGlobalStore(getUserRoles);
  const location = useLocation();

  const { setCacheSeed, setHasLineItems } = useCampaignStore() as CampaignsStore;

  const { user: userId = '' } = session?.toJSON() ?? {};

  const [{ globalLoading, isLoading, modalStatus, options }, setState] = useState<State>({
    globalLoading: false,
    isLoading: false,
    modalStatus: true,
    options: {
      inheritCreativeConfigFromIO: false,
      inheritMapConfigFromIO: false,
      numberOfEntitiesToCreate: 1
    }
  });

  const {
    lineItemId,
    mode
  }: {
    lineItemId?: string;
    mode: string;
  } = location.state;

  const showSelectorStep = !!location.state.showSelectorStep;

  const [selectedInsertionOrderId, setSelectedInsertionOrderId] = useState<string | undefined>(
    location.state.insertionOrderId
  );
  const [selectedCampaignPlanId, setSelectedCampaignPlanId] = useState<string | undefined>(
    location.state.campaignPlanId
  );

  const { data: insertionOrder, isLoading: insertionOrderIsLoading } = useQueryFetch(
    ['ioSettings_insertionOrder', selectedInsertionOrderId],
    () => getInsertionOrder(userId, selectedInsertionOrderId!, {}),
    {
      enabled: !!selectedInsertionOrderId,
      staleTime: 0
    }
  );

  const { data: campaignPlan, isLoading: campaignPlanIsLoading } = useQueryFetch(
    ['ioSettings_campaignPlan', selectedCampaignPlanId!],
    () => getCampaignPlan(userId, selectedCampaignPlanId!, {}),
    {
      enabled: !!selectedCampaignPlanId,
      staleTime: 0
    }
  );

  const { data: lineItem, isLoading: lineItemIsLoading } = useQueryFetch(
    ['ioSettings_lineItem', lineItemId],
    () => getLineItem(lineItemId!),
    {
      enabled: !!lineItemId,
      staleTime: 0
    }
  );

  const initialLoading = insertionOrderIsLoading || campaignPlanIsLoading || lineItemIsLoading;

  const lineItemEntity = mode === 'create' ? new LineItemEntity() : new LineItemEntity(lineItem);

  if (mode === 'create' && !initialLoading && selectedInsertionOrderId && selectedCampaignPlanId) {
    lineItemEntity.setDefaultCampaignSettingsFromUser(user);
    lineItemEntity.set('insertionOrder', selectedInsertionOrderId);
    lineItemEntity.setMultiflightEntitiesSettings(campaignPlan!, insertionOrder!.toJSON());
  }

  const handleModalClose = () => {
    modals.close({
      at: {
        pathname: '/campaigns',
        search: location.search
      }
    });
    setState(prevState => ({
      ...prevState,
      modalStatus: false
    }));
  };

  const handleModalFinish = async (campaignValues: Partial<LineItem>) => {
    if (mode === 'create') {
      await lineItemEntity.setCampaignDetailsData(campaignValues);
      setState(prevState => ({
        ...prevState,
        globalLoading: true,
        isLoading: true
      }));
      const lineItemData = await createLineItem(lineItemEntity.toJSON(), options);
      handleModalClose();
      if (Array.isArray(lineItemData)) {
        setHasLineItems(true);
        setCacheSeed();
        navigate('/campaigns?tab=lineItem' as any);
        return;
      }
      const lineItemSaved = lineItemData.toJSON();
      const { _id } = lineItemSaved;
      navigate('/liSettings/edit/:id', {
        params: { id: _id },
        state: {
          insertionOrder: insertionOrder?.toJSON(),
          lineItem: lineItemSaved,
          originPath: location.pathname + location.search
        }
      });
    } else {
      setState(prevState => ({
        ...prevState,
        isLoading: true
      }));
      const newLineItem = await updateLineItem(campaignValues);
      queryClient.setQueryData(['ioSettings_lineItem', lineItemId], newLineItem);
      setState(prevState => ({
        ...prevState,
        globalLoading: true,
        isLoading: false
      }));
      setCacheSeed();
      handleModalClose();
    }
  };

  const handleSelectorSelectionChange = (campaignPlanId?: string, insertionOrderId?: string) => {
    setSelectedCampaignPlanId(campaignPlanId);
    setSelectedInsertionOrderId(insertionOrderId);
  };

  const setOptions = (optionsParam: State['options']) =>
    setState(prevState => ({
      ...prevState,
      options: optionsParam
    }));

  const showFullPageSpinner = globalLoading || (!showSelectorStep && initialLoading);

  if (showFullPageSpinner) {
    return (
      <div id="backgroundSpinnerContainer">
        <Loader
          backgroundColor="var(--color-sonata-primary-200, #F2F5FC)"
          showBackground
          type="landing"
          variant="default"
        />
      </div>
    );
  }

  let newLineItem = lineItemEntity.toJSON();
  if (mode === 'create') {
    newLineItem = { ...newLineItem, countries: insertionOrder?.toJSON().countries ?? [] };
  }

  return (
    <LineItemWizard
      campaign={newLineItem}
      currencyInfo={currencyInfo}
      isLoading={isLoading}
      isModalOpened={modalStatus}
      isSelectorDataLoading={insertionOrderIsLoading || campaignPlanIsLoading}
      mode={mode as 'create' | 'edit'}
      onClose={handleModalClose}
      onFinish={handleModalFinish}
      onSelectorSelectionChange={handleSelectorSelectionChange}
      options={options}
      setOptions={setOptions}
      showSelectorStep={showSelectorStep}
      user={{
        _id: userId,
        id: userId,
        ...user.toJSON()
      }}
      userRoles={userRoles}
    />
  );
}
